import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import clsx from "clsx";
import { NAV } from "@/data/site";
import { PillButton } from "@/components/primitives";
import { GovXLogo } from "@/components/GovXLogo";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={clsx(
        "fixed inset-x-0 top-0 z-50 transition-colors duration-500",
        scrolled ? "bg-govx-paper/85 backdrop-blur-xl border-b border-govx-charcoal/10" : "bg-transparent"
      )}
    >
      <div className="mx-auto flex h-[74px] w-full max-w-[1320px] items-center justify-between px-5 sm:px-8 lg:px-12">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <GovXLogo variant={scrolled ? "dark" : "light"} />
        </Link>

        <nav className="hidden items-center gap-7 xl:flex">
          {NAV.map((item) => {
            const active = pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace(/\//g, "")}`}
                className={clsx(
                  "relative text-[0.82rem] font-semibold tracking-tight transition-colors duration-200",
                  active
                    ? "text-govx-green"
                    : scrolled
                    ? "text-govx-charcoal/70 hover:text-govx-charcoal"
                    : "text-govx-paper/75 hover:text-white"
                )}
              >
                {item.label}
                {active && <span className="absolute -bottom-1.5 left-0 h-px w-full bg-govx-green" />}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden xl:block">
            <PillButton to="/contact" data-testid="nav-cta" className="px-5 py-2.5">
              Start a Conversation
            </PillButton>
          </div>
          <button
            data-testid="nav-menu-toggle"
            className={clsx(
              "flex h-10 w-10 items-center justify-center rounded-full border xl:hidden",
              scrolled ? "border-govx-charcoal/15 text-govx-charcoal" : "border-white/25 text-govx-paper"
            )}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-govx-charcoal/10 bg-govx-paper xl:hidden"
          >
            <div className="flex flex-col px-5 py-4 sm:px-8">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="border-b border-govx-charcoal/8 py-3.5 text-lg font-medium tracking-tight text-govx-charcoal"
                >
                  {item.label}
                </Link>
              ))}
              <PillButton to="/contact" className="mt-5 w-full justify-center">
                Start a Conversation
              </PillButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
